import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  PayrollPaymentListItem,
  PayrollPaymentListResponse,
  PayrollPaymentVm,
  GetPaymentAmountResponse,
  GetPaymentAmountEmployeeItem,
  PayrollPaymentLineItem,
} from '@/api/types/payroll.types'
import type { ApiResponse } from '@/api/types'

/**
 * Convert YYYY-MM (form) to MM-YYYY for PayrollPayment API (e.g. 2026-02 -> 02-2026)
 */
export function toPayrollPaymentMonthYear(ym: string): string {
  if (!ym || ym.length < 7) return ym
  const [y, m] = ym.split('-')
  return `${m}-${y}`
}

export const payrollPaymentService = {
  getAll(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<PayrollPaymentListResponse> {
    const url = endpoints.payrollPayment.getAll(branchId, pageNumber, pageSize)
    return apiClient.get<ApiResponse<PayrollPaymentListResponse> | PayrollPaymentListResponse>(url).then((res) => {
      const raw = res.data as any
      const arr = raw?.data ?? (Array.isArray(raw) ? raw : [])
      const list = (Array.isArray(arr) ? arr : []).map((item: any): PayrollPaymentListItem => ({
        id: item.id ?? item.Id,
        paymentNo: item.paymentNo ?? item.PaymentNo ?? '',
        payrollMonthYear: item.payrollMonthYear ?? item.PayrollMonthYear,
        date: item.date ?? item.Date,
        paymentDate: item.paymentDate ?? item.PaymentDate ?? item.date ?? item.Date,
        amount: item.amount ?? item.Amount,
        branchId: item.branchId ?? item.BranchId,
        creditAccount: item.creditAccount ?? item.CreditAccount,
        creditAccountName: item.creditAccountName ?? item.CreditAccountName,
      }))
      return {
        data: list,
        totalRecords: raw?.totalRecords ?? list.length,
        pageNumber: raw?.pageNumber ?? pageNumber,
        pageSize: raw?.pageSize ?? pageSize,
      }
    })
  },

  getById(id: number): Promise<{ payrollMonthYear: string; date: string; amount: number; branchId: number }> {
    const url = endpoints.payrollPayment.getById(id)
    return apiClient.get(url).then((res) => {
      const raw = res.data as any
      if (!raw || typeof raw !== 'object') return raw
      const dateVal = raw.date ?? raw.Date ?? raw.paymentDate ?? raw.PaymentDate ?? ''
      return {
        id: raw.id ?? raw.Id,
        payrollMonthYear: raw.payrollMonthYear ?? raw.PayrollMonthYear ?? '',
        paymentDate: dateVal,
        date: dateVal,
        amount: raw.amount ?? raw.Amount ?? 0,
        branchId: raw.branchId ?? raw.BranchId ?? 0,
      }
    })
  },

  getLinesByPaymentId(id: number): Promise<PayrollPaymentLineItem[]> {
    const url = endpoints.payrollPayment.getLinesByPaymentId(id)
    return apiClient.get(url).then((res) => {
      const raw = res.data as any
      const arr = Array.isArray(raw) ? raw : (raw?.data ?? raw?.Data ?? [])
      if (!Array.isArray(arr)) return []
      return arr.map((item: any): PayrollPaymentLineItem => ({
        id: item.id ?? item.Id,
        paymentId: item.paymentId ?? item.PaymentId,
        empId: item.empId ?? item.EmpId ?? 0,
        amount: item.amount ?? item.Amount ?? 0,
        remainingAmount: item.remainingAmount ?? item.RemainingAmount ?? 0,
        branchId: item.branchId ?? item.BranchId,
      }))
    })
  },

  getByMonthYear(monthYear: string, branchId: number): Promise<PayrollPaymentVm | null> {
    const apiMonthYear = toPayrollPaymentMonthYear(monthYear)
    const url = endpoints.payrollPayment.getByMonthYear(apiMonthYear, branchId)
    return apiClient.get<PayrollPaymentVm>(url).then((res) => {
      const raw = res.data as any
      if (!raw || typeof raw !== 'object') return null
      const details = raw.payrollPaymentDetails ?? raw.PayrollPaymentDetails ?? []
      const detailsList = Array.isArray(details) ? details.map((d: any) => ({
        employeeId: d.employeeId ?? d.EmployeeId ?? 0,
        amount: d.amount ?? d.Amount ?? 0,
      })) : []
      return {
        id: raw.id ?? raw.Id,
        payrollMonthYear: raw.payrollMonthYear ?? raw.PayrollMonthYear ?? '',
        paymentDate: raw.paymentDate ?? raw.PaymentDate ?? '',
        amount: raw.amount ?? raw.Amount ?? 0,
        branchId: raw.branchId ?? raw.BranchId ?? 0,
        payrollPaymentDetails: detailsList,
      }
    })
  },

  getPaymentAmount(monthYear: string, departmentId: number, branchId: number): Promise<GetPaymentAmountResponse> {
    const apiMonthYear = toPayrollPaymentMonthYear(monthYear)
    const url = endpoints.payrollPayment.getPaymentAmount(apiMonthYear, departmentId, branchId)
    return apiClient.get<GetPaymentAmountResponse>(url).then((res) => {
      const raw = res.data as any
      const arr = Array.isArray(raw) ? raw : (raw?.data ?? raw?.Data ?? [])
      if (!Array.isArray(arr)) return []
      return arr.map((item: any): GetPaymentAmountEmployeeItem => ({
        employeeId: item.employeeId ?? item.EmployeeId ?? item.employerId ?? item.EmployerId ?? 0,
        empName: item.empName ?? item.EmpName ?? item.employeeName ?? item.EmployeeName ?? '—',
        totalAmount: item.totalAmount ?? item.TotalAmount ?? 0,
        remainingAmount: item.remainingAmount ?? item.RemainingAmount ?? 0,
      }))
    })
  },

  create(payload: PayrollPaymentVm): Promise<unknown> {
    return apiClient.post(endpoints.payrollPayment.create, payload).then((r) => r.data)
  },

  update(payload: PayrollPaymentVm): Promise<unknown> {
    return apiClient.put(endpoints.payrollPayment.update, payload).then((r) => r.data)
  },

  delete(id: number): Promise<unknown> {
    const url = endpoints.payrollPayment.delete(id)
    return apiClient.delete(url).then((r) => r.data)
  },
}
